/**
 * useBrokerAccounts Hook
 * Hook for managing broker accounts
 * 
 * ⚠️ IMPORTANT: Per ADR-FE-001, this hook only fetches and displays broker account data.
 * All broker account operations are handled by backend Broker Integration Service.
 */

import { useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import {
  fetchBrokerAccounts,
  fetchSupportedBrokers,
  disconnectBrokerAccount,
  deregisterBrokerAccount,
  reconnectBrokerAccount,
  toggleAccountSelection,
  selectAllAccounts,
  deselectAllAccounts,
} from '../../../store/slices/brokerAccountsSlice';

export function useBrokerAccounts() {
  const dispatch = useAppDispatch();
  const brokerAccounts = useAppSelector((state) => state.brokerAccounts);

  /**
   * Fetch broker accounts from backend
   */
  const loadAccounts = useCallback(async () => {
    try {
      const result = await dispatch(fetchBrokerAccounts());
      return result;
    } catch (error) {
      console.error('[useBrokerAccounts] loadAccounts error:', error);
      throw error;
    }
  }, [dispatch]);

  /**
   * Fetch supported brokers list
   */
  const loadSupportedBrokers = useCallback(async () => {
    await dispatch(fetchSupportedBrokers());
  }, [dispatch]);

  /**
   * Disconnect a broker account (soft delete)
   */
  const disconnectAccount = useCallback(
    async (registrationId: string) => {
      await dispatch(disconnectBrokerAccount(registrationId));
      // Refresh accounts list after disconnection
      await loadAccounts();
    },
    [dispatch, loadAccounts]
  );

  /**
   * Deregister a broker account (permanent deletion)
   */
  const deregisterAccount = useCallback(
    async (registrationId: string) => {
      await dispatch(deregisterBrokerAccount(registrationId));
      // Refresh accounts list after deregistration
      await loadAccounts();
    },
    [dispatch, loadAccounts]
  );

  /**
   * Reconnect a broker account
   * Routes to automated TOTP login or OAuth flow based on account auth_mode
   */
  const reconnectAccount = useCallback(
    async (registrationId: string, forceRefresh: boolean = false) => {
      const result = await dispatch(reconnectBrokerAccount({ registrationId, forceRefresh })).unwrap();
      
      // Check auth_mode to determine flow
      if (result.auth_mode === 'totp_automated') {
        // TOTP automated reconnect - refresh list to show updated status
        await loadAccounts();
      } else if (result.oauth_url) {
        // OAuth flow - redirect to OAuth URL
        window.location.href = result.oauth_url;
      } else {
        // Account is already connected, refresh the list
        await loadAccounts();
      }
      return result;
    },
    [dispatch, loadAccounts]
  );

  /**
   * Toggle account selection
   */
  const toggleSelection = useCallback(
    (accountId: string) => {
      dispatch(toggleAccountSelection(accountId));
    },
    [dispatch]
  );

  /**
   * Select all accounts
   */
  const selectAll = useCallback(() => {
    dispatch(selectAllAccounts());
  }, [dispatch]);

  /**
   * Deselect all accounts
   */
  const deselectAll = useCallback(() => {
    dispatch(deselectAllAccounts());
  }, [dispatch]);

  /**
   * Get selected accounts
   */
  const selectedAccounts = brokerAccounts.accounts.filter((acc) =>
    brokerAccounts.selectedAccountIds.includes(acc.id)
  );

  /**
   * Load accounts on mount
   * Note: This auto-load is disabled to avoid conflicts with component-level loading
   * Components should explicitly call loadAccounts() when needed
   */
  // useEffect(() => {
  //   if (brokerAccounts.accounts.length === 0 && !brokerAccounts.loading) {
  //     console.log('[useBrokerAccounts] Auto-loading accounts...');
  //     loadAccounts();
  //   }
  // }, [loadAccounts, brokerAccounts.accounts.length, brokerAccounts.loading]);

  return {
    accounts: brokerAccounts.accounts,
    selectedAccounts,
    selectedAccountIds: brokerAccounts.selectedAccountIds,
    loading: brokerAccounts.loading,
    error: brokerAccounts.error,
    syncStatus: brokerAccounts.syncStatus,
    loadAccounts,
    loadSupportedBrokers,
    disconnectAccount,
    deregisterAccount,
    reconnectAccount,
    toggleSelection,
    selectAll,
    deselectAll,
  };
}

